// src/components/manuscriptTheme/ThemeRailCard.jsx
//
// The Theme box in the editor's right rail: which theme the manuscript is set
// in, a thumbnail of it, and the way into the Theme Editor. It took over from a
// display-only placeholder ("genre/theme isn't wired into the editor yet").
//
// Switching themes is not here — the ticket moved that to a button in the format
// bar and removed the prototype's dropdown from this box.
//
// Below the card, the prototype's consistency line: how many places in the
// manuscript carry their own font or size, which would sit on top of the theme,
// and a button that clears them.
import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { tokens } from '../../theme';
import { BREAK_STYLES } from '../../config/manuscriptThemes';
import { artName, artOn, completeTheme } from '../../utils/manuscriptTheme';
import ThemeMiniCard from './ThemeMiniCard';

const chipSx = {
  fontSize: 11.5,
  lineHeight: 1.3,
  px: 1,
  py: 0.5,
  borderRadius: `${tokens.radius.base}px`,
  background: tokens.color.lineOnDark,
  color: tokens.color.onDark,
};

export default function ThemeRailCard({
  theme,
  sample,
  trimLabel,
  canChange,
  onCustomize,
  overrideCount = 0,
  onReset,
}) {
  const t = theme ? completeTheme(theme) : null;
  const chips = t ? [
    trimLabel,
    `${t.body.font} ${t.body.size}/${(t.body.size * t.body.lead).toFixed(1)}`,
    t.body.align === 'justify' ? 'Justified' : 'Ragged right',
    `Breaks: ${(BREAK_STYLES.find(([k]) => k === t.brk.style) || [])[1] || ''}`,
    artOn(t) ? `Art: ${artName(t.art)} ${t.art.opacity}%` : '',
  ].filter(Boolean) : [];

  return (
    <Box sx={{ mb: 2.75 }}>
      <Typography sx={{ fontSize: 13, color: tokens.color.card, mb: 1 }}>
        {theme ? theme.name : 'No theme'}
      </Typography>

      <Box sx={{ background: 'rgba(0, 0, 0, 0.14)', borderRadius: `${tokens.radius.card}px`, p: 1.25 }}>
        {t ? (
          <ThemeMiniCard theme={t} title={sample?.title} excerpt={sample?.excerpt} height={150} />
        ) : (
          <Typography sx={{ fontSize: 12, color: tokens.color.onDark, lineHeight: 1.5, p: 0.5 }}>
            The manuscript uses its own formatting. A theme sets the type, margins and
            chapter openings for the whole book at once.
          </Typography>
        )}

        {chips.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, my: 1.25 }}>
            {chips.map((c) => <Box key={c} sx={chipSx}>{c}</Box>)}
          </Box>
        )}

        {canChange ? (
          <Button
            fullWidth
            onClick={onCustomize}
            startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{
              mt: t ? 0 : 1.25,
              background: tokens.color.card,
              color: tokens.color.purpleDeep,
              borderRadius: `${tokens.radius.panel}px`,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 13,
              '&:hover': { background: tokens.color.purpleTint },
            }}
          >
            Customize theme
          </Button>
        ) : (
          <Typography sx={{ fontSize: 11.5, color: tokens.color.onDark, mt: 1 }}>
            Only the author can change the theme.
          </Typography>
        )}
      </Box>

      {t && (
        <Typography component="div" sx={{ mt: 1.25, fontSize: 12, lineHeight: 1.5, color: tokens.color.onDark }}>
          {overrideCount > 0 ? (
            <>
              <Box component="span" sx={{ color: tokens.color.warningOnDark, fontWeight: 600 }}>
                {overrideCount} {overrideCount === 1 ? 'place has its' : 'places have their'} own font or size
              </Box>
              {' '}that hides the theme.
              {canChange && (
                <Button
                  onClick={onReset}
                  sx={{
                    display: 'block', p: 0, mt: 0.5, minWidth: 0,
                    color: tokens.color.card, textTransform: 'none', fontSize: 12,
                    textDecoration: 'underline',
                    '&:hover': { background: 'transparent', textDecoration: 'underline' },
                  }}
                >
                  Reset to theme
                </Button>
              )}
            </>
          ) : (
            <Box component="span" sx={{ color: tokens.color.successOnDark, fontWeight: 600 }}>
              ✓ One theme across the whole manuscript
            </Box>
          )}
        </Typography>
      )}
    </Box>
  );
}
